import { FC, ReactNode } from 'react'
import { Navigate, Outlet } from 'react-router-dom'

import { useIsAdmin } from 'app/features/admin/useIsAdmin'
import { RouteUrl } from '../interface'

interface AdminRouteProps {
    /**
     * Optional protected content. When omitted, the guard renders an
     * `<Outlet />` so it can be used as a layout/parent route element the
     * same way `AuthenticatedRoute` is.
     */
    children?: ReactNode
}

/**
 * Route guard that only exposes its subtree to admin users.
 *
 * Renders `children` (or the nested route `<Outlet />` when used as a layout
 * route) when {@link useIsAdmin} returns `true`, and otherwise redirects to
 * the existing {@link RouteUrl.Unauthorized} route.
 *
 * Mirrors the `AuthenticatedRoute` pattern: a thin wrapper whose sole job is
 * access control, delegating admin determination to the centralized
 * `useIsAdmin()` hook so the gating source can be swapped in one place.
 *
 * Secure-by-default: a non-admin (including unauthenticated) user never sees
 * the protected content — the redirect uses `replace` so the guarded URL does
 * not linger in history.
 */
export const AdminRoute: FC<AdminRouteProps> = ({ children }) => {
    const isAdmin = useIsAdmin()

    if (!isAdmin) {
        return <Navigate to={RouteUrl.Unauthorized.path} replace />
    }

    return <>{children ?? <Outlet />}</>
}
